// src/store/playback/selectors.js

import { createSelector } from '@reduxjs/toolkit'
import { diff } from 'deep-object-diff'
import { EMPTY_ARRAY } from '~/lib/constants'
import { selectProjectData } from '~/store/app/selectors'
import { selectQueries } from '~/store/api/selectors'
import { selectLive } from '~/store/live/selectors'
import { selectShowExamplePresets } from '~/store/settings/selectors'
import { unpinnedSettings } from '~/lib/livePins'
import { pinKeysOf } from '~/lib/pinLayer'
import { EFFECT_SCHEMAS } from '~/lib/presetSettings'
import { MORPH_TO, isMorphEnvelope } from '~/schemas/morph'
import { morphToPlain } from '~/lib/utils'

/**
 * Keys that live on an effect but are not params of it, excluded from the dirty comparison.
 *
 * An explicit table rather than a rule, for the same reason the chop's relocation map is one: both entries are accidents of history with no shared property, and a heuristic that happened to cover them today would silently mis-fire on the next one.
 *
 * `objects` belongs at top level but the server nests it inside diffusion when broadcasting;
 * `advanced` is ephemeral (stripped on save/load) but leaks into old presets. TODO: remove after server fix + preset migration script (see diffusion.objects in handleDCMessage.js)
 */
export const NON_PARAM_KEYS = { diffusion: ['objects', 'advanced'] }

// Selectors

export const selectPresets = createSelector(
	selectProjectData, // Pass the whole state for the endpoint selector
	projectData => {
		if (!projectData) return EMPTY_ARRAY

		const { project, presets } = projectData

		// Create a map of the fetched presets for quick lookup
		const presetMap = presets.reduce((acc, p) => {
			if (p?.id) {
				acc[p.id] = p
			}
			return acc
		}, {})

		// Build the ordered list using the map and the required IDs
		const ordered = []
		for (const presetId of project.presets) {
			const preset = presetMap[presetId]
			if (preset) ordered.push(preset)
		}
		return ordered
	}
)

export const selectPlayback = s => s.playback

export const selectCurrentPresetIndex = createSelector(selectPlayback, p => p.current_preset_index)

// ⚠️ A morph is interpolated by the SERVER in embedding space and the client only ever held the endpoints, so no scalar names an interior point and saving one stores a state that never rendered.
export const selectLiveInvalid = createSelector(selectPlayback, p => Object.keys(p.morphs ?? {}).length > 0)

/**
 * The preset a save would target — `-1` when none can claim the current state.
 *
 * ⚠️ NOT `selectCurrentPresetIndex`, which keeps naming the origin cell so the bar and the highlight stay drawn through a fade.
 */
export const selectSavePresetIndex = createSelector(selectCurrentPresetIndex, selectLiveInvalid, (index, invalid) =>
	invalid ? -1 : index
)

export const selectNextPresetIndex = createSelector(selectPlayback, p => p.next_preset_index)

export const selectExamplePresets = createSelector(selectQueries, selectPlayback, (queries, playback) => {
	// First try RTK Query cache
	const rtkData = queries['getExamplePresets(undefined)']?.data
	if (rtkData?.length) return rtkData
	// Fall back to cached templates in playback slice (for instant display from IndexedDB)
	return playback.cached_templates || EMPTY_ARRAY
})

export const selectDesktopExamplePresets = createSelector(selectQueries, selectPlayback, (queries, playback) => {
	// First try RTK Query cache
	const rtkData = queries['getDesktopExamplePresets(undefined)']?.data
	if (rtkData?.length) return rtkData
	// Fall back to cached desktop templates in playback slice (for instant display from IndexedDB)
	return playback.cached_desktop_templates || EMPTY_ARRAY
})

/** Does the project's playlist run on into the desktop examples? */
export const selectPlaylistExamples = s => !!selectProjectData(s)?.project?.settings?.playlist_examples

/**
 * What playback indexes: the presets, then the examples when the project includes them and this machine shows them. Only the runner's setting shapes autoplay.
 *
 * ⚠️ Playback only — `current_preset_index`, cues, runs and fades. Editing (save, delete, reorder, copy) stays on `selectPresets`: an example is read-only.
 */
const selectPresetsAndExamples = createSelector(selectPresets, selectDesktopExamplePresets, (presets, examples) =>
	examples.length ? [...presets, ...examples] : presets
)
export const selectPlaylist = s =>
	selectPlaylistExamples(s) && selectShowExamplePresets(s) ? selectPresetsAndExamples(s) : selectPresets(s)

// With unified index: indices >= userPresets.length are template presets
export const selectIsTemplatePresetActive = createSelector(
	selectPresets,
	selectCurrentPresetIndex,
	(presets, index) => index >= 0 && index >= presets.length
)

// Returns the template preset index (0-based within template presets array), or -1 if not a template preset
export const selectTemplatePresetIndex = createSelector(selectPresets, selectCurrentPresetIndex, (presets, index) =>
	index >= presets.length ? index - presets.length : -1
)

export const selectCurrentPreset = createSelector(selectPresets, selectCurrentPresetIndex, (presets, index) => {
	if (index === -1 || index >= presets.length) return null
	return presets[index]
})

export const selectIsTransitioning = createSelector(selectPlayback, p => p.next_preset_index >= 0)

/**
 * The ids committed to the fade in flight — its origin and its destination.
 *
 * Empty at rest and during a hold. A run's fade child and a standalone fade both name their endpoints in these two indices.
 */
export const selectFadingPresetIds = createSelector(selectPlaylist, selectPlayback, (presets, p) => {
	if (p.next_preset_index < 0) return EMPTY_ARRAY
	return [presets[p.current_preset_index]?.id, presets[p.next_preset_index]?.id].filter(Boolean)
})

export const selectInstant = createSelector(selectPlayback, p => p.instant)

export const selectPlaying = createSelector(selectPlayback, p => p.playing)

export const selectLoop = createSelector(selectPlayback, p => p.loop)

export const selectHoldDuration = createSelector(selectPlayback, p => p.hold_duration)

export const selectFadeDuration = createSelector(selectPlayback, p => p.fade_duration)

export const selectTransitionPaused = createSelector(selectPlayback, p => p.transition_paused)

export const selectTransitionDetached = createSelector(selectPlayback, p => p.transition_detached)

export const selectTransitionReversed = createSelector(selectPlayback, p => p.transition_reversed)

/**
 * Which two cells the transition bar spans; how far along it is comes off the timeline (`transitionManager.getProgress()`).
 *
 * `null` when nothing is transitioning, so the bar falls back to covering the current preset whole.
 */
export const selectTransitionBar = createSelector(
	selectCurrentPresetIndex,
	selectNextPresetIndex,
	selectTransitionPaused,
	selectTransitionDetached,
	selectTransitionReversed,
	(from, to, paused, detached, reversed) =>
		// `from: -1` when detached: a retargeted fade starts from a mid-fade snapshot, so no cell may draw the trailing part. The bar is then only what has arrived in the target — which is also why the two parts stop summing to one cell for that fade.
		to < 0 ? null : { from: detached ? -1 : from, to, paused, reversed }
)

export const selectPins = s => s.playback?.pins

/** The pin layer as live-prop keys; stable while the layer is. */
export const selectLiveProps = createSelector(selectPins, pins => {
	const keys = pinKeysOf(pins)
	return keys.length ? keys : EMPTY_ARRAY
})

// Every running morph, keyed by qualified param. `{}` at rest.
export const selectMorphs = createSelector(selectPlayback, p => p.morphs)

/**
 * One param's live morph envelope `{a, b, t}`, or undefined at rest.
 *
 * ⚠️ A HOOK FACTORY, not a selector — call it inside `useSelector`, e.g. `useSelector(selectMorph('diffusion.prompt'))`. Memoizing per key would need a cache keyed by the argument; the read is a single property lookup, so identity is the envelope object itself and re-renders only when it actually changes.
 */
export const selectMorph = key => s => s.playback.morphs[key]

const EMPTY_MORPH_ENDPOINT = { morphing: false, to: undefined, from: '' }

/**
 * `selectMorph` for a control that does not draw the travel. Read under `shallowEqual`.
 *
 * ⚠️ WITHOUT `t`, the only field that moves every frame — the whole envelope re-renders the control at the fade's frame rate.
 */
export const selectMorphEndpoint = key => s => {
	const m = s.playback.morphs[key]
	return m ? { morphing: true, to: m[MORPH_TO], from: m.from ?? '' } : EMPTY_MORPH_ENDPOINT
}

/**
 * May the current state be saved?
 *
 * ⚠️ An EMPTY project is always savable — Save creates the first preset and there is nothing to diff.
 *
 * ⚠️ A RUNNING fade is values in flight; only a PARKED one is a state the user chose.
 */
export const selectPresetChanged = createSelector(
	selectPresets,
	selectPlaylist,
	selectSavePresetIndex,
	selectNextPresetIndex,
	selectTransitionPaused,
	selectExamplePresets,
	selectLive,
	selectLiveProps,
	(presets, playlist, current_preset_index, next_preset_index, paused, templatePresets, live, live_props) => {
		if (!presets?.length) return true
		if (next_preset_index >= 0 && !paused) return false
		if (current_preset_index < 0) return false

		// Unified index: past the playlist are the mobile template presets.
		const preset = playlist[current_preset_index] ?? templatePresets[current_preset_index - playlist.length] ?? null

		if (!preset) {
			// No preset selected (neither user nor template)
			return false
		}

		// ⚠️ A pin is never a difference: never exclude one here, only in `unpinnedSettings`.
		const presetSide = unpinnedSettings(preset.settings ?? {}, live_props)
		const liveSide = unpinnedSettings(live, live_props)

		// A DIRECT DIFF of two sparse shapes. Storage and memory are one shape (§9 invariant 3), so `{}`, a missing key and a key-at-initial are the same state on both sides and there is nothing to reconcile — the union-of-keys construction this replaces existed only to fill a dense side from the contract.
		//
		// ⚠️ Only the EFFECT key carries presence; a param's absence is not a fact of its own. Leaving the effect key off the side that lacks it is what makes a removal read dirty. Materializing an absent effect as `{}` here would make removing one invisible, and it would be lost on the next load.
		//
		// Within a section this diff IS sensitive to key existence, which is only sound because absence means at-initial on BOTH sides — every writer prunes an at-initial param away (`setPruned`/`prune`). A single writer that skips the rule strands a dense at-initial key here and the preset reads permanently dirty, unfixable by saving: `stripSettings` keeps the stored side sparse, so the difference survives every round-trip.
		const normalized = { preset: {}, live: {} }
		const DROPPED = {}
		for (const effect of Object.keys(EFFECT_SCHEMAS)) {
			const excluded = NON_PARAM_KEYS[effect] ?? EMPTY_ARRAY

			// Keys that sit on an effect without being params of it.
			const omit = section => {
				if (!excluded.length) return section
				const out = { ...section }
				for (const k of excluded) delete out[k]
				return out
			}

			// A section only the omit emptied is gone, as `prune` drops an emptied effect: absence is its one spelling.
			const kept = raw => {
				const section = omit(raw)
				if (!raw || typeof raw !== 'object' || !Object.keys(raw).length) return section
				return Object.keys(section).length ? section : DROPPED
			}

			const presetSection = effect in presetSide ? kept(presetSide[effect]) : DROPPED
			if (presetSection !== DROPPED) normalized.preset[effect] = presetSection
			// A morphing param settles to its DESTINATION before comparing; raw, `{a,b,t,f}` differs from the preset's plain string every tick and reads dirty.
			//
			// DEFENSIVE: an envelope already makes `selectSavePresetIndex` `-1`, which returns early above.
			const section = effect in liveSide ? kept(liveSide[effect]) : DROPPED
			if (section !== DROPPED) {
				let settled = section
				for (const k in section) {
					if (!isMorphEnvelope(section[k])) continue
					if (settled === section) settled = { ...section }
					settled[k] = morphToPlain(section[k])
				}
				normalized.live[effect] = settled
			}
		}

		// Loops are conformed on both sides (the cache door, the reducers), so one spelling each.
		const presetSettings = {
			...normalized.preset,
			objects: presetSide.objects,
			modulators: presetSide.modulators,
			source_settings: presetSide.source_settings,
		}
		const liveSettings = {
			...normalized.live,
			objects: liveSide.objects,
			modulators: liveSide.modulators,
			source_settings: liveSide.source_settings,
		}

		return Object.keys(diff(presetSettings, liveSettings)).length > 0
	}
)

/**
 * Whether any of `ids` is committed to the fade in flight.
 *
 * ⚠️ Refuses the WHOLE operation: a multi-preset delete that silently skipped the fading one would leave the user's selection half-applied.
 */
export const touchesFadingPreset = (state, ids) => {
	const fading = selectFadingPresetIds(state)
	if (!fading.length) return false
	return (Array.isArray(ids) ? ids : [ids]).some(id => fading.includes(id))
}
